import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useActorName } from '@/hooks/use-actor-name'
import { toast } from 'sonner'
import { Check, Mail, Send, X } from 'lucide-react'
import { type ApInvoiceAction, type ApInvoiceThread } from '@/features/invoices/view-models'
import { type ApInvoiceAssignedUser } from '@/features/invoices/types'
import {
  useDecideManufacturerTerms,
  useResolveInvoiceAdjustment,
  useResolveInvoiceAction,
  useDismissInvoiceAdjustment,
  useSendInvoiceActionReply,
  useInvoiceActionReplyRecipients,
} from '@/features/invoices/data/actions'
import { useSendMessage, useUpdateMessage } from '@/features/communications/hooks'
import { ACCOUNTING_SIGNOFF } from '@/features/communications/formatting'
import { LoadingButton } from '@/components/loading-button'
import { RecordPaymentPanel } from '@/components/record-payment-panel'
import { type ActionInvoice } from '@/features/invoices/data/action-payments'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { StaffAssignmentPicker } from '@/components/staff-assignment-picker'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { stripQuotedText } from '@/lib/email-body'
import { cn, formatEventTime, formatMoney, formatStatus } from '@/lib/utils'
import {
  ActionCardTop,
  ActionDetails,
  ActionFacts,
} from '@/features/purchase-orders/action-card-parts'
import { roleLabel, type ActionSourceEmail } from '@/features/purchase-orders/action-card-model'
import { compactDate, parseEmailList } from './formatting'
import { InvoiceActionNotes } from './notes'
import type { InvoiceNotesContext } from './notes-context'
import { ChaseActions } from '@/features/owed-items/chase-actions'
import { ACTION_FOCUS_CLASS, actionTargetProps } from '@/hooks/use-action-focus'
import { ComposeTextarea } from '@/components/compose-field'
import { InlineFieldInput } from '@/components/inline-field-group'
import { EmailRecipientsField } from '@/components/email-recipients-field'
import { useEmailRecipients } from '@/components/use-email-recipients'
import { getApiErrorMessage } from '@/lib/api-error'

type ApInvoiceMessage = NonNullable<ApInvoiceThread['messages']>[number]

/** A plain link down to the email that raised the item, in this invoice's conversation (USA-1418). */
export function InvoiceSourceEmailLink({
  email,
  invoiceId,
}: {
  email: ActionSourceEmail
  invoiceId: string
}) {
  const sender = email.from_name?.trim()
  return (
    <Link
      to="/invoices/$invoiceId"
      params={{ invoiceId }}
      search={{ message: String(email.message_id) }}
      className="font-medium text-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
    >
      {sender ? `${sender}'s email` : 'the email'}
      {email.at && `, ${formatEventTime(email.at)}`} →
    </Link>
  )
}

function formatActionResolutionValue(field: string, value: string | null | undefined) {
  if (!value) return 'Not set'
  return /amount|total|tax|freight/.test(field) ? formatMoney(value) : value
}

/** Invoice operations the page owns, offered on the cards (USA-1418). */
export interface InvoiceCardOperations {
  /** Approve the invoice (the PM, or Accounting when it has no PM); absent when this user can't. */
  approve?: () => void
  approvePending?: boolean
  requestAdjustment?: (reason: string) => void
  requestAdjustmentPending?: boolean
  recordPayment?: () => void
  /** The invoice as a payment item records it inline, with proof (USA-103). */
  paymentInvoice?: ActionInvoice
  /** The duplicate decision, opened from the given button (USA-1628). */
  duplicateDecision?: (trigger: ReactNode) => ReactNode
  acceptTotal?: (amount: string, onDone: () => void) => void
}

type ReplyOption = { option_type: string; title?: string; reply_body?: string; amount?: string }

const OPTION_LABELS: Record<string, string> = {
  request_corrected_invoice: 'Request corrected invoice',
  request_credit_memo: 'Ask for credit memo',
  request_invoice_pdf: 'Ask for the PDF',
  ask_for_po: 'Ask which PO',
  confirm_payment: 'Confirm payment',
}

// The step that settles each kind of item when nothing needs saying to the vendor.
const DONE_LABELS: Record<string, string> = {
  adjustment_needed: 'Correction received',
  credit_review: 'Credit applied',
  serial_mapping_review: 'Serials recorded',
  freight_variance_review: 'Accept carrier bill',
  freight_allocation_review: 'Allocation done',
  vendor_link_needed: 'Vendor confirmed',
  invoice_expectation_fulfillment_review: 'Split recorded',
}

function readOptions(structured: Record<string, unknown>): ReplyOption[] {
  const raw = structured.resolution_options
  return Array.isArray(raw)
    ? raw.filter(
        (option): option is ReplyOption =>
          !!option && typeof option === 'object' && typeof option.option_type === 'string',
      )
    : []
}

/** What staff can do with an accounting item, as the PO cards offer it: one primary step, then the rest. */
function InvoiceActionChoices({
  action,
  invoiceId,
  structured,
  operations,
  canUpdate,
}: {
  action: ApInvoiceAction
  invoiceId: string
  structured: Record<string, unknown>
  operations?: InvoiceCardOperations
  canUpdate: boolean
}) {
  const resolveAction = useResolveInvoiceAction(invoiceId)
  const sendReply = useSendInvoiceActionReply(invoiceId)
  const [reply, setReply] = useState<{ type: string; label: string; body: string } | null>(null)
  const replyRecipientsQuery = useInvoiceActionReplyRecipients(invoiceId, action.id, reply !== null)
  const replyRecipients = useEmailRecipients(
    replyRecipientsQuery.data ?? (replyRecipientsQuery.isError ? { to: [], cc: [] } : undefined),
  )
  const [adjustmentReason, setAdjustmentReason] = useState<string | null>(null)
  const [paying, setPaying] = useState(false)
  const type = action.action_type
  const isApproval = [
    'approval_needed',
    'operations_approval_needed',
    'finance_approval_needed',
    'approval_reminder',
  ].includes(type)
  // A late owed item (USA-1423): chased with a new date, received, or no longer needed.
  if (type === 'owed_overdue') {
    return canUpdate ? <ChaseActions owner={{ source: 'ap_invoice', invoiceUuid: invoiceId }} actionId={action.id} /> : null
  }

  const resolve = (status: 'resolved' | 'dismissed', resolutionType?: string) =>
    resolveAction.mutate(
      { actionId: action.id, status, resolution_type: resolutionType },
      {
        onSuccess: () => toast.success(status === 'resolved' ? 'Done' : 'Dismissed'),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Update failed')),
      },
    )
  const openReply = (option: ReplyOption) =>
    setReply({
      type: option.option_type,
      label: option.title || OPTION_LABELS[option.option_type] || 'Reply',
      body: option.reply_body ?? '',
    })

  const choices: {
    key: string
    label: string
    run?: () => void
    loading?: boolean
    /** Opens its own popover from the button instead of running on click. */
    wrap?: (button: ReactNode) => ReactNode
  }[] = []
  if (isApproval && operations?.approve) {
    choices.push({ key: 'approve', label: 'Approve', run: operations.approve, loading: operations.approvePending })
  }
  if (isApproval && operations?.requestAdjustment) {
    choices.push({ key: 'adjust', label: 'Request adjustment', run: () => setAdjustmentReason('') })
  }
  // A supplier asking about payment, or the invoice's pay day (USA-1183): both are paid from the item.
  const paysInvoice = type === 'payment_followup' || type === 'payment_due'
  const payable =
    paysInvoice && action.status === 'open' && operations?.paymentInvoice?.record_payment
      ? operations.paymentInvoice
      : null
  if (payable) {
    choices.push({ key: 'pay', label: 'Record payment', run: () => setPaying(true) })
  } else if (paysInvoice && operations?.recordPayment) {
    choices.push({ key: 'pay', label: 'Record payment', run: operations.recordPayment })
  }
  if (type === 'po_link_needed') {
    choices.push({
      key: 'link',
      label: 'Link PO',
      run: () =>
        document
          .getElementById('po-project-match-heading')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
    })
  }
  if (type === 'attachment_review') {
    choices.push({
      key: 'upload',
      label: 'Upload PDF',
      run: () => document.getElementById('ap-invoice-upload')?.click(),
    })
  }
  if (type === 'duplicate_review' && operations?.duplicateDecision) {
    choices.push({ key: 'duplicate', label: 'Decide duplicate', wrap: operations.duplicateDecision })
  }
  if (canUpdate) {
    for (const [index, option] of readOptions(structured).entries()) {
      if (option.option_type === 'accept_new_total' && option.amount && operations?.acceptTotal) {
        const amount = option.amount
        choices.push({
          key: `option-${index}`,
          label: `Accept ${formatMoney(amount)}`,
          run: () => operations.acceptTotal?.(amount, () => resolve('resolved', 'accept_new_total')),
        })
      } else if (option.option_type !== 'accept_new_total') {
        choices.push({
          key: `option-${index}`,
          label: option.title || OPTION_LABELS[option.option_type] || 'Reply',
          run: () => openReply(option),
        })
      }
    }
    const doneLabel = DONE_LABELS[type] ?? (choices.length === 0 ? 'Mark done' : null)
    if (doneLabel && !isApproval) {
      choices.push({
        key: 'done',
        label: doneLabel,
        run: () => resolve('resolved', 'done'),
        loading: resolveAction.isPending && resolveAction.variables?.status === 'resolved',
      })
    }
    choices.push({
      key: 'reply',
      label: 'Write reply',
      run: () => openReply({ option_type: 'custom_reply', title: 'Reply to vendor' }),
    })
  }
  if (choices.length === 0) return null

  return (
    <div className="space-y-3 border-t pt-3">
      {payable && paying && (
        <RecordPaymentPanel
          invoice={payable}
          actionSource="ap_invoice"
          actionId={action.id}
          onCancel={() => setPaying(false)}
        />
      )}
      {reply === null && adjustmentReason === null && !paying && (
        <div className="flex flex-wrap gap-2">
          {choices.map((choice, index) => {
            const button = (
              <LoadingButton
                size="sm"
                variant={index === 0 ? 'default' : 'outline'}
                loading={choice.loading ?? false}
                onClick={choice.run}
              >
                {index === 0 && <Check className="mr-1 h-3 w-3" />}
                {choice.label}
              </LoadingButton>
            )
            return <Fragment key={choice.key}>{choice.wrap ? choice.wrap(button) : button}</Fragment>
          })}
          {canUpdate && (
            <LoadingButton
              size="sm"
              variant="ghost"
              loading={resolveAction.isPending && resolveAction.variables?.status === 'dismissed'}
              onClick={() => resolve('dismissed')}
            >
              <X className="mr-1 h-3 w-3" /> Dismiss
            </LoadingButton>
          )}
        </div>
      )}
      {adjustmentReason !== null && (
        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
          <Label htmlFor={`adjustment-reason-${action.id}`}>What needs correcting?</Label>
          <ComposeTextarea
            id={`adjustment-reason-${action.id}`}
            value={adjustmentReason}
            onChange={(event) => setAdjustmentReason(event.target.value)}
            rows={3}
            autoFocus
          />
          <div className="flex flex-wrap gap-2">
            <LoadingButton
              size="sm"
              loading={operations?.requestAdjustmentPending ?? false}
              disabled={!adjustmentReason.trim()}
              onClick={() => operations?.requestAdjustment?.(adjustmentReason.trim())}
            >
              Request adjustment
            </LoadingButton>
            <Button size="sm" variant="ghost" onClick={() => setAdjustmentReason(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {reply && (
        <div className="space-y-3 rounded-md border bg-muted/30 p-3">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <p className="text-sm font-medium">{reply.label}</p>
          </div>
          <EmailRecipientsField
            state={replyRecipients}
            fallback="The vendor on this conversation"
            disabled={sendReply.isPending}
          />
          <div className="space-y-1">
            <Label htmlFor={`invoice-reply-${action.id}`}>Reply</Label>
            <ComposeTextarea
              id={`invoice-reply-${action.id}`}
              value={reply.body}
              onChange={(event) => setReply({ ...reply, body: event.target.value })}
              rows={6}
              className="min-h-[120px]"
              autoFocus
            />
            <p className="whitespace-pre-line text-xs text-muted-foreground">{ACCOUNTING_SIGNOFF.trim()}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <LoadingButton
              size="sm"
              loading={sendReply.isPending}
              loadingText="Sending..."
              disabled={!reply.body.trim()}
              onClick={() =>
                sendReply.mutate(
                  {
                    actionId: action.id,
                    bodyText: reply.body.trim(),
                    resolutionType: reply.type,
                    recipients: replyRecipients.override,
                  },
                  {
                    onSuccess: () => {
                      toast.success('Reply sent')
                      setReply(null)
                      replyRecipients.reset()
                    },
                    onError: (err) => toast.error(getApiErrorMessage(err, 'Reply could not be sent')),
                  },
                )
              }
            >
              <Send className="mr-1 h-3 w-3" /> Send reply
            </LoadingButton>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setReply(null)
                replyRecipients.reset()
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export function ActionItemCard({
  action,
  invoiceId,
  actorRole,
  draftMessage,
  history = false,
  underSourceEmail = false,
  operations,
  notes,
}: {
  action: ApInvoiceAction
  invoiceId: string
  /** Where comments on the item go: the invoice's notes, tagged with the item (USA-1180). */
  notes?: InvoiceNotesContext
  actorRole?: string | null
  draftMessage?: ApInvoiceMessage
  history?: boolean
  /** Shown under the email that raised it, so it doesn't link back to that email. */
  underSourceEmail?: boolean
  operations?: InvoiceCardOperations
}) {
  const actorName = useActorName()
  const resolveActionMutation = useResolveInvoiceAction(invoiceId)
  const resolveAdjustmentMutation = useResolveInvoiceAdjustment(invoiceId)
  const dismissAdjustmentMutation = useDismissInvoiceAdjustment(invoiceId)
  const termsDecisionMutation = useDecideManufacturerTerms(invoiceId)
  const sendMessageMutation = useSendMessage()
  const updateMessageMutation = useUpdateMessage()
  // Nothing opens until staff pick a step; the draft email shows only once they choose to send it.
  const [adjustmentMode, setAdjustmentMode] = useState<'send' | 'resolve' | 'dismiss' | null>(null)
  const actionMetadata = useMemo(() => {
    try {
      const value = JSON.parse(action.structured_data || '{}')
      return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
    } catch {
      return {}
    }
  }, [action.structured_data])
  const resolutionMetadata = useMemo(() => {
    try {
      const value = JSON.parse(action.resolution_data || '{}')
      return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
    } catch {
      return {}
    }
  }, [action.resolution_data])
  const expectedCorrectionAmount =
    typeof actionMetadata.expected_amount === 'string' &&
    Number.isFinite(Number(actionMetadata.expected_amount))
      ? Number(actionMetadata.expected_amount).toFixed(2)
      : ''
  const [correctedInvoiceTotal, setCorrectedInvoiceTotal] = useState(expectedCorrectionAmount)
  const [correctedTaxAmount, setCorrectedTaxAmount] = useState('0')
  const [correctedFreightAmount, setCorrectedFreightAmount] = useState(
    action.category === 'freight' ? expectedCorrectionAmount : '',
  )
  const [correctedShipTo, setCorrectedShipTo] = useState('')
  const [resolutionNote, setResolutionNote] = useState('')
  const [dismissalReason, setDismissalReason] = useState('')
  const isAdjustmentAction =
    action.action_type === 'invoice_adjustment' || action.action_type === 'tax_adjustment_needed'
  const isTermsDecision = action.action_type === 'manufacturer_terms_changed'
  const draftRecipients = draftMessage ? parseEmailList(draftMessage.to_emails) : []
  const correctionRecipients = useEmailRecipients(
    draftMessage ? { to: draftRecipients, cc: parseEmailList(draftMessage.cc_emails) } : undefined,
  )
  const draftBody = draftMessage?.body_text ? stripQuotedText(draftMessage.body_text) : ''
  const [draftText, setDraftText] = useState(draftBody)
  const correctionRequestSent = draftMessage?.status === 'sent'
  const manufacturerUuid =
    isTermsDecision && typeof actionMetadata.manufacturer_uuid === 'string'
      ? actionMetadata.manufacturer_uuid
      : null
  const headline = action.card?.headline ?? action.summary
  // The header names the role, never a person.
  const owners = [roleLabel(action.assigned_role ?? 'accounting')]
  const financeOnlyAction = [
    'unexpected_tax',
    'missing_evidence',
    'missing_document',
    'document_mismatch',
    'credit_exception',
    'write_off_required',
  ].includes(action.action_type)
  const canUpdateAction =
    (['ap_preparer', 'accounting', 'finance_approver', 'ap_admin'].includes(actorRole || '') ||
      (action.category === 'pricing' && actorRole === 'project_coordinator')) &&
    (!financeOnlyAction || ['finance_approver', 'ap_admin'].includes(actorRole || ''))
  const canManageAdjustment = ['accounting', 'ap_admin'].includes(actorRole || '')
  const canCompleteAdjustment =
    actorRole === 'ap_admin' ||
    (action.category === 'pricing'
      ? actorRole === 'project_coordinator'
      : actorRole === 'accounting')
  const canEditAdjustment = canManageAdjustment || canCompleteAdjustment

  const submitAdjustmentResolution = () => {
    if (!resolutionNote.trim()) {
      toast.error('Add a note describing the corrected invoice')
      return
    }
    resolveAdjustmentMutation.mutate(
      {
        actionId: action.id,
        body: {
          notes: resolutionNote.trim(),
          corrected_invoice_total:
            action.category === 'ship_to' ? undefined : correctedInvoiceTotal || undefined,
          corrected_tax_amount:
            action.category === 'tax' ? correctedTaxAmount || undefined : undefined,
          corrected_freight_amount:
            action.category === 'freight' ? correctedFreightAmount || undefined : undefined,
          corrected_ship_to:
            action.category === 'ship_to' ? correctedShipTo.trim() || undefined : undefined,
        },
      },
      {
        onSuccess: () => toast.success('Correction recorded and returned to Accounting review'),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, 'Correction could not be recorded')),
      },
    )
  }

  const submitAdjustmentDismissal = () => {
    if (!dismissalReason.trim()) {
      toast.error('Explain why this correction request is being dismissed')
      return
    }
    dismissAdjustmentMutation.mutate(
      { actionId: action.id, reason: dismissalReason.trim() },
      {
        onSuccess: () =>
          toast.success('Correction request dismissed and returned to Accounting review'),
        onError: (err) =>
          toast.error(
            getApiErrorMessage(err, 'Correction request could not be dismissed'),
          ),
      },
    )
  }

  const decideTerms = (decision: 'update' | 'keep') => {
    termsDecisionMutation.mutate(
      { actionId: action.id, decision },
      {
        onSuccess: () =>
          toast.success(
            decision === 'update' ? 'Manufacturer terms updated' : 'Kept our payment terms',
          ),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, 'Payment terms decision failed')),
      },
    )
  }

  const updateOwner = (user: ApInvoiceAssignedUser | null) => {
    if (!user) {
      toast.error('Adjustment work must have an owner')
      return
    }
    resolveActionMutation.mutate(
      {
        actionId: action.id,
        assigned_user_id: user.id,
        assigned_role: action.category === 'pricing' ? 'operations' : 'accounting',
      },
      {
        onSuccess: () => toast.success('Adjustment owner updated'),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Owner update failed')),
      },
    )
  }

  const updateDueDate = (dueDate: string) => {
    if (!dueDate || dueDate === action.due_date) return
    resolveActionMutation.mutate(
      { actionId: action.id, due_date: dueDate },
      {
        onSuccess: () => toast.success('Follow-up date updated'),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Date update failed')),
      },
    )
  }

  return (
    <Card
      {...actionTargetProps(action.uuid)}
      className={cn(
        ACTION_FOCUS_CLASS,
        underSourceEmail && 'gap-0 rounded-none border-0 bg-transparent py-0 shadow-none',
      )}
    >
      <CardContent className={underSourceEmail ? 'space-y-3 px-0' : 'space-y-3 py-4'}>
      <div className="flex flex-col items-stretch gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <ActionCardTop
            card={action.card}
            actionType={action.action_type}
            status={action.status}
            owners={owners}
            due={action.due_date}
            createdAt={action.created_at ?? action.updated_at ?? ''}
          />
          <p className="break-words text-[15px] font-semibold leading-snug">
            {manufacturerUuid ? (
              <Link
                to="/manufacturers/$manuId"
                params={{ manuId: manufacturerUuid }}
                className="hover:underline"
              >
                {headline}
              </Link>
            ) : (
              headline
            )}
          </p>
          {action.card && <ActionFacts facts={action.card.facts} />}
          {action.card?.note && (
            <p className="break-words text-sm text-foreground/80">{action.card.note}</p>
          )}
          {!history && canEditAdjustment && action.category && (
            <div className="flex flex-wrap items-center gap-3 pt-1">
              {canManageAdjustment ? (
                <StaffAssignmentPicker
                  label="Owner"
                  currentUser={action.assigned_user}
                  currentUserId={action.assigned_user_id}
                  allowedRoles={
                    action.category === 'pricing'
                      ? ['operations', 'project_coordinator']
                      : ['accounting']
                  }
                  pending={resolveActionMutation.isPending}
                  showName
                  onChange={updateOwner}
                />
              ) : (
                <span className="text-xs text-muted-foreground">
                  Owner: {action.assigned_user?.name || `Staff #${action.assigned_user_id}`}
                </span>
              )}
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">Follow up by</span>
                <POInlineEditableField
                  kind="date"
                  value={action.due_date || ''}
                  displayValue={action.due_date ? compactDate(action.due_date) : undefined}
                  placeholder="Add date"
                  ariaLabel="Follow up by"
                  title="Follow up by"
                  pending={resolveActionMutation.isPending}
                  buttonClassName="min-w-0 text-xs"
                  inputClassName="h-7 w-auto"
                  onConfirm={updateDueDate}
                />
              </div>
            </div>
          )}
          {!history && canCompleteAdjustment && action.status === 'open' && isAdjustmentAction && (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Choose the next step</p>
              <ReviewChoiceGroup
                label="Choose the next step for this invoice correction"
                value={adjustmentMode === 'dismiss' ? null : adjustmentMode}
                options={[
                  ...(draftMessage
                    ? [
                        {
                          value: 'send' as const,
                          label: correctionRequestSent
                            ? 'Correction request sent'
                            : 'Send correction request',
                        },
                      ]
                    : []),
                  {
                    value: 'resolve' as const,
                    label: 'Record corrected invoice',
                  },
                ]}
                onChange={setAdjustmentMode}
                className="flex w-full flex-col sm:flex-row"
                optionClassName="flex-1 justify-center whitespace-normal py-2 text-center"
              />
            </div>
          )}
          {!history && isAdjustmentAction && draftMessage && adjustmentMode === 'send' && (
            <div className="mt-3 space-y-3 rounded-md border bg-muted/30 p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <Mail className="h-4 w-4" /> Correction request
                </p>
                <Badge variant={correctionRequestSent ? 'secondary' : 'outline'}>
                  {correctionRequestSent ? 'Sent' : 'Draft'}
                </Badge>
              </div>
              {!correctionRequestSent && canCompleteAdjustment && (
                <EmailRecipientsField
                  state={correctionRecipients}
                  fallback="Recipient missing"
                  disabled={updateMessageMutation.isPending || sendMessageMutation.isPending}
                />
              )}
              <dl className="grid gap-1 text-xs sm:grid-cols-[4rem_1fr]">
                {(correctionRequestSent || !canCompleteAdjustment) && (
                  <>
                    <dt className="text-muted-foreground">To</dt>
                    <dd className="break-words">{draftRecipients.join(', ') || 'Recipient missing'}</dd>
                  </>
                )}
                <dt className="text-muted-foreground">Subject</dt>
                <dd className="break-words">{draftMessage.subject || 'No subject'}</dd>
              </dl>
              {correctionRequestSent ? (
                <div className="whitespace-pre-wrap rounded-md border bg-background p-3 text-sm">
                  {draftBody || 'No message body recorded.'}
                </div>
              ) : (
                <ComposeTextarea
                  aria-label="Correction request"
                  value={draftText}
                  onChange={(event) => setDraftText(event.target.value)}
                  rows={6}
                  className="min-h-[120px]"
                />
              )}
              <div className="flex flex-wrap gap-2">
                {!correctionRequestSent && canCompleteAdjustment && (
                  <LoadingButton
                    size="sm"
                    loading={
                      updateMessageMutation.isPending ||
                      (sendMessageMutation.isPending &&
                        sendMessageMutation.variables === draftMessage.uuid)
                    }
                    loadingText="Sending..."
                    onClick={() => {
                      const send = () =>
                        sendMessageMutation.mutate(draftMessage.uuid, {
                          onSuccess: () => toast.success('Correction request sent to the vendor'),
                          onError: (err) =>
                            toast.error(
                              err instanceof Error
                                ? err.message
                                : 'Correction request could not be sent',
                            ),
                        })
                      const textChanged = draftText.trim() !== draftBody.trim()
                      if (!textChanged && !correctionRecipients.changed) return send()
                      updateMessageMutation.mutate(
                        {
                          messageUuid: draftMessage.uuid,
                          body: {
                            ...(textChanged ? { body_text: draftText } : {}),
                            ...correctionRecipients.override,
                          },
                        },
                        {
                          onSuccess: send,
                          onError: (err) =>
                            toast.error(getApiErrorMessage(err, 'Draft could not be saved')),
                        },
                      )
                    }}
                  >
                    <Send className="mr-1 size-3.5" /> Send email to vendor
                  </LoadingButton>
                )}
              </div>
            </div>
          )}
          {!history &&
            canCompleteAdjustment &&
            action.status === 'open' &&
            isAdjustmentAction &&
            adjustmentMode !== 'dismiss' && (
              <Button
                size="sm"
                variant="link"
                className="h-auto px-0 pt-2 text-xs text-muted-foreground"
                onClick={() => setAdjustmentMode('dismiss')}
              >
                No correction needed? Close this request
              </Button>
            )}
          {history &&
            Boolean(resolutionMetadata.changes) &&
            typeof resolutionMetadata.changes === 'object' && (
              <div className="space-y-1 pt-1 text-xs text-muted-foreground">
                {Object.entries(
                  resolutionMetadata.changes as Record<string, Record<string, string | null>>,
                ).map(([field, values]) => (
                  <p key={field}>
                    {formatStatus(field)}: {formatActionResolutionValue(field, values.previous)} to{' '}
                    {formatActionResolutionValue(field, values.corrected)}
                  </p>
                ))}
                {typeof resolutionMetadata.notes === 'string' && <p>{resolutionMetadata.notes}</p>}
              </div>
            )}
        </div>

        {!history && canUpdateAction && isTermsDecision && action.status === 'open' && (
          <div className="flex flex-wrap gap-2 border-t pt-3">
            <LoadingButton
              size="sm"
              loading={
                termsDecisionMutation.isPending &&
                termsDecisionMutation.variables?.decision === 'update'
              }
              loadingText="Updating..."
              disabled={termsDecisionMutation.isPending}
              onClick={() => decideTerms('update')}
            >
              Update manufacturer terms
            </LoadingButton>
            <LoadingButton
              size="sm"
              variant="outline"
              loading={
                termsDecisionMutation.isPending &&
                termsDecisionMutation.variables?.decision === 'keep'
              }
              loadingText="Keeping..."
              disabled={termsDecisionMutation.isPending}
              onClick={() => decideTerms('keep')}
            >
              Keep ours
            </LoadingButton>
          </div>
        )}
        {!history && !isAdjustmentAction && !isTermsDecision && action.status === 'open' && (
          <InvoiceActionChoices
            action={action}
            invoiceId={invoiceId}
            structured={actionMetadata}
            operations={operations}
            canUpdate={canUpdateAction}
          />
        )}
      </div>
      {!history && isAdjustmentAction && adjustmentMode === 'resolve' && (
        <div className="mt-4 space-y-4 rounded-lg border bg-muted/20 p-4">
          <div>
            <p className="text-sm font-medium">Record the corrected invoice</p>
            <p className="text-xs text-muted-foreground">
              Saving the correction closes this work and restarts Accounting review.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {action.category === 'tax' && (
              <div className="space-y-2">
                <Label htmlFor={`corrected-tax-${action.id}`}>Corrected tax</Label>
                <InlineFieldInput
                  id={`corrected-tax-${action.id}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={correctedTaxAmount}
                  onChange={(event) => setCorrectedTaxAmount(event.target.value)}
                />
              </div>
            )}
            {action.category === 'freight' && (
              <div className="space-y-2">
                <Label htmlFor={`corrected-freight-${action.id}`}>Corrected freight</Label>
                <InlineFieldInput
                  id={`corrected-freight-${action.id}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={correctedFreightAmount}
                  onChange={(event) => setCorrectedFreightAmount(event.target.value)}
                />
              </div>
            )}
            {action.category === 'ship_to' ? (
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor={`corrected-ship-to-${action.id}`}>Corrected ship-to</Label>
                <InlineFieldInput
                  id={`corrected-ship-to-${action.id}`}
                  value={correctedShipTo}
                  onChange={(event) => setCorrectedShipTo(event.target.value)}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor={`corrected-total-${action.id}`}>Corrected invoice total</Label>
                <InlineFieldInput
                  id={`corrected-total-${action.id}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={correctedInvoiceTotal}
                  onChange={(event) => setCorrectedInvoiceTotal(event.target.value)}
                />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor={`resolution-note-${action.id}`}>Resolution note</Label>
            <ComposeTextarea
              id={`resolution-note-${action.id}`}
              value={resolutionNote}
              onChange={(event) => setResolutionNote(event.target.value)}
              placeholder="Revised invoice received from vendor"
            />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setAdjustmentMode(null)}>
              Cancel
            </Button>
            <LoadingButton
              loading={resolveAdjustmentMutation.isPending}
              loadingText="Recording..."
              onClick={submitAdjustmentResolution}
            >
              Save corrected invoice
            </LoadingButton>
          </div>
        </div>
      )}
      {!history && isAdjustmentAction && adjustmentMode === 'dismiss' && (
        <div className="mt-4 space-y-4 rounded-lg border bg-muted/20 p-4">
          <div>
            <p className="text-sm font-medium">Close without a corrected invoice?</p>
            <p className="text-xs text-muted-foreground">
              Use this only when the request is no longer valid. No invoice values will change, and
              Accounting will review it again.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`dismissal-reason-${action.id}`}>Why is no correction needed?</Label>
            <ComposeTextarea
              id={`dismissal-reason-${action.id}`}
              value={dismissalReason}
              onChange={(event) => setDismissalReason(event.target.value)}
            />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setAdjustmentMode(null)}>
              Cancel
            </Button>
            <LoadingButton
              variant="destructive"
              loading={dismissAdjustmentMutation.isPending}
              loadingText="Dismissing..."
              onClick={submitAdjustmentDismissal}
            >
              Close without correction
            </LoadingButton>
          </div>
        </div>
      )}
      {notes && (
        <InvoiceActionNotes action={action} context={notes} canComment={!history && action.status === 'open'} />
      )}
      <div className="space-y-1 border-t pt-2.5 text-xs text-muted-foreground">
        <p className="break-words">
          <span className="font-medium text-foreground">Raised</span>
          {action.created_by && ` by ${actorName(action.created_by)}`} ·{' '}
          {action.created_at && formatEventTime(action.created_at)}
          {action.card?.source_email && !underSourceEmail && (
            <>
              {' · from '}
              <InvoiceSourceEmailLink email={action.card.source_email} invoiceId={invoiceId} />
            </>
          )}
        </p>
        {action.resolved_at && (
          <p className="break-words">
            <span className="font-medium text-foreground">{formatStatus(action.status)}</span>
            {action.resolved_by &&
              ` by ${
                action.category
                  ? action.assigned_user?.name || 'assigned owner'
                  : actorName(action.resolved_by)
              }`}{' '}
            · {formatEventTime(action.resolved_at)}
            {action.resolution_type &&
              ` · ${
                action.resolution_type.startsWith('record_')
                  ? `${formatStatus(action.category || 'invoice')} correction recorded`
                  : formatStatus(action.resolution_type)
              }`}
          </p>
        )}
        <ActionDetails summary={action.summary} details={action.details} headline={action.card?.headline ?? action.summary} />
      </div>
      </CardContent>
    </Card>
  )
}
